import { Paper, Text, Group, Stack, Badge } from '@mantine/core';
import { Milestone, MilestoneStatus } from '../../types/milestones';
import { StatusBadge } from '../common/StatusBadge';
import { IconCalendarDue } from '@tabler/icons-react';
import dayjs from 'dayjs';

// Status configuration
const statusConfig = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' }
};

// Simple Status Badge Component
const SimpleStatusBadge = ({ status }: { status: MilestoneStatus }) => {
  const config = statusConfig[status] || statusConfig.not_started;
  
  return (
    <Badge 
      color={config.color} 
      variant="filled" 
      size="sm"
      style={{ padding: '8px 12px' }}
    >
      {config.label}
    </Badge>
  );
};

interface MilestoneStatusCardProps {
  milestone: Milestone;
  activityTitle?: string;
  onToggle: (id: string, status: MilestoneStatus) => void;
}

export function MilestoneStatusCard({ 
  milestone, 
  activityTitle, 
  onToggle 
}: Readonly<MilestoneStatusCardProps>) {
  const isPastDue = milestone.status !== 'completed' && dayjs(milestone.due_date).isBefore(dayjs(), 'day');
  
  // Extract numbers from activity_id and milestone id to create "1.1" format
  const getActivityNumber = (activityId: string | undefined): string => {
    if (!activityId) return '0';
    const match = activityId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const getMilestoneNumber = (milestoneId: string): string => {
    const match = milestoneId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const activityNumber = getActivityNumber(milestone.activity_id);
  const milestoneNumber = getMilestoneNumber(milestone.id);
  const milestoneLabel = `${activityNumber}.${milestoneNumber}`;
  
  return (
    <Paper 
      shadow="sm" 
      p="md" 
      radius="md" 
      withBorder
      sx={(theme) => ({
        height: '280px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        '&:hover': {
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]
        }
      })}
    >
      <Stack spacing={4} style={{ flex: 1 }}>
        {/* Top section with milestone badge and status badge */}
        <Group position="apart" noWrap mb="md">
          <Badge size="sm" color="gray" variant="filled">{`Milestone ${milestoneLabel}`}</Badge>
          <SimpleStatusBadge status={milestone.status} />
        </Group>
        
        {/* Milestone title with adequate height for 4 lines */}
        <Text 
          weight={500} 
          size="md" 
          lineClamp={4}
          mb="md"
          sx={(theme) => ({
            textDecoration: milestone.completed ? 'line-through' : 'none',
            color: milestone.completed ? theme.colors.gray[6] : 'inherit',
            height: '100px', // Fixed height for title area
            overflow: 'hidden',
          })}
        >
          {milestone.title}
        </Text>
        
        {/* Due date */}
        <Group spacing="xs" noWrap mb={12}>
          <IconCalendarDue size={16} style={{ color: 'var(--mantine-color-dimmed)' }} />
          <Text 
            size="sm" 
            color="dimmed" 
            weight={500}
            sx={milestone.completed ? { textDecoration: 'line-through' } : {}}
          >
            {dayjs(milestone.due_date).format('MMM D, YYYY')}
          </Text>
        </Group>
        
        {/* Bottom section with status badge */}
        <Group position="apart" mt="auto" mb={8} style={{ minHeight: '32px' }}>
          {isPastDue && <StatusBadge status="past_due" />}
          <div></div> {/* Empty div to push items apart when status is not shown */}
        </Group>
      </Stack>
    </Paper>
  );
} 